/* eslint-disable @typescript-eslint/no-explicit-any */
import * as yup from "yup";

import { MediaType } from "../../../../constants/enums";
import { DirectUploadMedia } from "../../../../libs/directUpload/directUpload.types";
import { ProductVideoVariant } from "../../constants/enums";

export const productValidationSchema = yup.object({
  description: yup
    .string()
    .required("Product description is required")
    .min(
      10,
      "The minimum length of the description field should be at least 10 characters."
    ),
  googleDriveLink: yup
    .string()
    .nullable()
    .transform((value) => value || null)
    .url("Google Drive link must be a valid URL"),
  isChapshopChoice: yup.boolean(),
  sizes: yup
    .array()
    .required()
    .min(1, "Product should have at least one size")
    .of(
      yup.object({
        sizeAttributeValueId: yup.string().required(),
        label: yup.string().required()
      })
    ),
  productVideoVariant: yup
    .mixed<ProductVideoVariant>()
    .oneOf(Object.values(ProductVideoVariant)),
  generalVideo: yup.array().when("productVideoVariant", {
    is: ProductVideoVariant.ONE_MAIN_VIDEO,
    then: () =>
      yup
        .array()
        .required()
        .of(yup.object())
        .min(1, "Product should have one main video")
  }),
  relatedAttributesValues: yup
    .array()
    .required()
    .of(
      yup.object({
        attributeId: yup.string(),
        value: yup.string()
      })
    ),
  additionalAttributesValuesIds: yup
    .array()
    .required()
    .of(
      yup.object({
        attributeId: yup.string(),
        valueId: yup.string().required("Field is required")
      })
    ),
  colors: yup
    .array()
    .required()
    .of(yup.object({ value: yup.string().required() }))
    .min(1, "Product should have at least one color variant"),
  variants: yup
    .array()
    .required()
    .of(
      yup.object({
        name: yup
          .string()
          .required("Product name is required")
          .test(
            "no-leading-space",
            "Product name cannot start with a space.",
            (value) => {
              if (!value) return true;
              return value.charAt(0) !== " ";
            }
          )
          .test(
            "no-consecutive-spaces",
            "Product name cannot contain consecutive space characters.",
            (value) => {
              if (!value) return true;
              return !/\s\s/.test(value);
            }
          )
          .min(5, "Product name should have more than 4 letters."),
        barcodes: yup
          .array()
          .of(
            yup.object({
              sizeAttributeValueId: yup.string().required(),
              value: yup.string().nullable()
            })
          )
          .required()
          .test({
            name: "atLeastOneBarcode",
            message: "At least one barcode is required",
            test: (barcodes) => {
              return barcodes.some((barcode) => barcode.value);
            }
          })
          .test({
            name: "uniqueBarcodes",
            message: "Barcodes should be unique for each size",
            test: (barcodes) => {
              if (barcodes.every((barcode) => !barcode.value)) {
                return true;
              }

              const seenValues = new Set();

              for (let i = 0; i < barcodes.length; i++) {
                const barcode = barcodes[i];

                if (barcode.value && seenValues.has(barcode.value)) {
                  return false; // Duplicate value found
                }

                seenValues.add(barcode.value);
              }

              return true; // All values are unique
            }
          }),
        images: yup
          .array()
          .required()
          .of(yup.object<DirectUploadMedia>())
          .min(1, "Product should have at least one image")
          .test({
            name: "atLeastOneImage",
            message: "Product should have at least one image",
            test: (images) => {
              return images?.some(
                (image: any) => image.mediaType === MediaType.IMAGE
              );
            }
          }),
        gif: yup.object(),
        weight: yup
          .number()
          .typeError("Product weight should be a number")
          .required(
            "Weight is a required field. Please provide this information"
          )
          .test(
            "Not-negative",
            "Weight cannot be a negative value.",
            (value) => value >= 0
          )
          .min(
            0.005,
            "Please enter a valid weight within the specified length. (0.005 - 300)"
          )
          .max(
            300,
            "Please enter a valid weight within the specified length. (0.005 - 300)"
          ),
        volume: yup
          .number()
          .typeError("Please enter a valid numeric value for the volume.")
          .required(
            "Volume is a required field. Please provide this information"
          )
          .test(
            "Not-negative",
            "Volume cannot be a negative value.",
            (value) => value >= 0
          )
          .min(0.1, "Volume should be between 0.1 cbm and 1000 cbm.")
          .max(1000, "Volume should be between 0.1 cbm and 1000 cbm."),
        buyingPrice: yup
          .number()
          .typeError("Bying price should be a number")
          .required("Bying price is required")
          .test(
            "Not-negative",
            "Buying price cannot be a negative value.",
            (value) => value >= 0
          )
          .min(
            10,
            "The minimum length of Buying price field should be at least 2 numbers."
          ),
        standardPrice: yup
          .number()
          .typeError("Standard price should be a number")
          .required("Standard price is required")
          .positive("Standard price should be positive")
          .min(
            10,
            "The minimum length of Standard price field should be at least 2 numbers."
          ),
        intermediateResellerPrice: yup
          .number()
          .typeError("Intermediate reseller price should be a number")
          .required("Intermediate reseller price is required")
          .positive("Intermediate reseller price should be positive")
          .min(
            10,
            "The minimum length of Intermediate reseller price field should be at least 2 numbers."
          )
          .test(
            "lowerThanStandardPrice",
            "The Intermediate reseller price can not be higher than the standard price",
            function (value) {
              const { standardPrice } = this.parent;
              return value <= standardPrice;
            }
          ),
        confirmedResellerPrice: yup
          .number()
          .typeError("Confirmed reseller price should be a number")
          .required("Confirmed reseller price is required")
          .positive("Confirmed reseller price should be positive")
          .min(
            10,
            "The minimum length of Confirmed reseller price field should be at least 2 numbers."
          )
          .test(
            "lowerThanIntermediateResellerPrice",
            "The Confirmed reseller price can not be higher than the intermediate reseller price",
            function (value) {
              const { intermediateResellerPrice } = this.parent;
              return value <= intermediateResellerPrice;
            }
          ),
        influencerPrice: yup
          .number()
          .typeError("Influencer price should be a number")
          .required("Influencer price is required")
          .positive("Influencer price should be positive")
          .min(
            10,
            "The minimum length of Influencer price field should be at least 2 numbers."
          )
          .test(
            "lowerThanConfirmedResellerPrice",
            "The Influencer price can not be higher than the confirmed reseller price",
            function (value) {
              const { confirmedResellerPrice } = this.parent;
              return value <= confirmedResellerPrice;
            }
          ),
        recommendedSellingPrice: yup
          .number()
          .optional()
          .nullable()
          .typeError("Recommended selling price should be a number")
          .positive("Recommended selling price should be positive")
          .min(
            10,
            "The minimum length of Recommended selling price field should be at least 2 numbers."
          ),
        merchantPrice: yup
          .number()
          .typeError("Merchant price should be a number")
          .required("Merchant price is required")
          .test(
            "Not-negative",
            "Merchant price cannot be a negative value.",
            (value) => value >= 0
          )
          .min(
            10,
            "The minimum length of Merchant price field should be at least 2 numbers."
          )
          .test(
            "lowerThanStandardPrice",
            "Merchant price can't be higher than standard price",
            function (value) {
              const { standardPrice } = this.parent;
              return value <= standardPrice;
            }
          ),
        discount: yup
          .number()
          .optional()
          .nullable()
          .typeError("Discount should be a number")
          .moreThan(-0.999999, "Discount should be positive")
          .max(95, "Discount should be not more than 95%"),
        discountAmount: yup
          .number()
          .optional()
          .nullable()
          .typeError("Discount should be a number")
          .moreThan(-0.999999, "Discount should be positive")
          .test(
            "lessThanBuyingPrice",
            "Discount cannot be greater than buying price",
            function (value) {
              if (!value) return true;
              const { buyingPrice } = this.parent;
              return value < buyingPrice;
            }
          ),
        suggestionDiscount: yup
          .number()
          .optional()
          .nullable()
          .typeError("Discount should be a number")
          .moreThan(-0.999999, "Discount should be positive")
          .max(95, "Discount should be not more than 95%"),
        suggestionDiscountAmount: yup
          .number()
          .optional()
          .nullable()
          .typeError("Discount should be a number")
          .moreThan(-0.999999, "Discount should be positive")
          .test(
            "lessThanMerchantPrice",
            "Discount cannot be greater than merchant price",
            function (value) {
              if (!value) return true;
              const { merchantPrice } = this.parent;
              return value < merchantPrice;
            }
          ),
        commissionPercentage: yup
          .number()
          .typeError("Comission should be a number")
          .optional()
          .nullable()
          .moreThan(-0.999999, "Comission should be positive")
          .max(95, "Comission should be not more than 95%"),
        commission: yup
          .number()
          .typeError("Comission should be a number")
          .optional()
          .nullable()
          .moreThan(-0.999999, "Comission should be positive"),
        bulkDiscounts: yup
          .array()
          .required()
          .of(
            yup.object({
              min: yup.number().required(),
              max: yup.number().required(),
              discount: yup
                .number()
                .required("Discount is required")
                .typeError("Discount should be a number")
                .moreThan(-0.999999, "Discount should be positive")
                .max(95, "Discount should be not more than 95%")
            })
          )
      })
    )
});
